import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { accountErrorKey } from '@/components/travel/cari/account-form-model';
import { Plus, Pencil, Trash2, Star, Building2 } from 'lucide-react';

interface BankAccountsTabProps {
  entityType: 'partner' | 'customer';
  entityId: string;
  canEdit?: boolean;
}

interface BankAccountForm {
  bankName: string;
  accountName: string;
  accountType: string;
  currency: string;
  branchCode: string;
  branchName: string;
  accountNo: string;
  iban: string;
  swift: string;
  isPrimary: boolean;
  notes: string;
}

const emptyForm: BankAccountForm = {
  bankName: '',
  accountName: '',
  accountType: '',
  currency: 'TRY',
  branchCode: '',
  branchName: '',
  accountNo: '',
  iban: '',
  swift: '',
  isPrimary: false,
  notes: '',
};

export function BankAccountsTab({ entityType, entityId, canEdit = false }: BankAccountsTabProps) {
  const { t } = useTranslation(['partners', 'common', 'travelCari']);
  const utils = trpc.useUtils();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<BankAccountForm>(emptyForm);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [bankNameError, setBankNameError] = useState(false);

  // Always call both hooks (React rules), but only use one
  const partnerQuery = trpc.partner.getBankAccounts.useQuery(
    { partnerId: entityId },
    { enabled: entityType === 'partner' }
  );
  const customerQuery = trpc.customer.getBankAccounts.useQuery(
    { customerId: entityId },
    { enabled: entityType === 'customer' }
  );

  const accounts = entityType === 'partner' ? partnerQuery.data : customerQuery.data;
  const isLoading = entityType === 'partner' ? partnerQuery.isLoading : customerQuery.isLoading;
  const readError = entityType === 'partner' ? partnerQuery.error : customerQuery.error;

  const partnerCreate = trpc.partner.createBankAccount.useMutation({
    onSuccess: () => {
      utils.partner.getBankAccounts.invalidate();
      closeForm();
    },
  });
  const customerCreate = trpc.customer.createBankAccount.useMutation({
    onSuccess: () => {
      utils.customer.getBankAccounts.invalidate();
      closeForm();
    },
  });
  const partnerUpdate = trpc.partner.updateBankAccount.useMutation({
    onSuccess: () => {
      utils.partner.getBankAccounts.invalidate();
      closeForm();
    },
  });
  const customerUpdate = trpc.customer.updateBankAccount.useMutation({
    onSuccess: () => {
      utils.customer.getBankAccounts.invalidate();
      closeForm();
    },
  });
  const partnerDelete = trpc.partner.deleteBankAccount.useMutation({
    onSuccess: () => {
      utils.partner.getBankAccounts.invalidate();
      setDeleteConfirmId(null);
    },
  });
  const customerDelete = trpc.customer.deleteBankAccount.useMutation({
    onSuccess: () => {
      utils.customer.getBankAccounts.invalidate();
      setDeleteConfirmId(null);
    },
  });

  const isPending =
    partnerCreate.isPending ||
    customerCreate.isPending ||
    partnerUpdate.isPending ||
    customerUpdate.isPending;
  const saveError =
    partnerCreate.error ||
    customerCreate.error ||
    partnerUpdate.error ||
    customerUpdate.error ||
    partnerDelete.error ||
    customerDelete.error;

  function closeForm() {
    setIsFormOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    setBankNameError(false);
  }

  function resetErrors() {
    partnerCreate.reset();
    customerCreate.reset();
    partnerUpdate.reset();
    customerUpdate.reset();
    partnerDelete.reset();
    customerDelete.reset();
    setBankNameError(false);
  }

  function openEdit(account: {
    id: string;
    bankName: string;
    accountName: string | null;
    accountType: string | null;
    currency: string;
    branchCode: string | null;
    branchName: string | null;
    accountNo: string | null;
    iban: string | null;
    swift: string | null;
    isPrimary: boolean;
    notes: string | null;
  }) {
    resetErrors();
    setForm({
      bankName: account.bankName || '',
      accountName: account.accountName || '',
      accountType: account.accountType || '',
      currency: account.currency || 'TRY',
      branchCode: account.branchCode || '',
      branchName: account.branchName || '',
      accountNo: account.accountNo || '',
      iban: account.iban || '',
      swift: account.swift || '',
      isPrimary: account.isPrimary,
      notes: account.notes || '',
    });
    setEditingId(account.id);
    setIsFormOpen(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canEdit || isPending) return;
    if (!form.bankName.trim()) {
      setBankNameError(true);
      return;
    }
    setBankNameError(false);
    const cleanData = {
      bankName: form.bankName.trim(),
      accountName: form.accountName.trim(),
      accountType: form.accountType.trim(),
      currency: form.currency,
      branchCode: form.branchCode.trim(),
      branchName: form.branchName.trim(),
      accountNo: form.accountNo.trim(),
      iban: form.iban.trim(),
      swift: form.swift.trim(),
      isPrimary: form.isPrimary,
      notes: form.notes.trim(),
    };

    if (editingId) {
      if (entityType === 'partner') partnerUpdate.mutate({ id: editingId, ...cleanData });
      else customerUpdate.mutate({ id: editingId, ...cleanData });
    } else {
      if (entityType === 'partner') partnerCreate.mutate({ partnerId: entityId, ...cleanData });
      else customerCreate.mutate({ customerId: entityId, ...cleanData });
    }
  }

  if (isLoading) {
    return (
      <div className="text-muted-foreground flex justify-center py-12">{t('common:loading')}</div>
    );
  }
  if (readError)
    return (
      <p role="alert" className="text-destructive text-sm">
        {t('travelCari:account.loadFailed')}
      </p>
    );

  return (
    <div className="space-y-4">
      {saveError && (
        <p role="alert" className="text-destructive text-sm">
          {t(`travelCari:account.errors.${accountErrorKey(saveError)}`)}
        </p>
      )}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          {t('partners:bankAccounts.title', 'Banka Hesapları')}
        </h3>
        {canEdit && (
          <button
            disabled={isPending}
            onClick={() => {
              resetErrors();
              setForm(emptyForm);
              setEditingId(null);
              setIsFormOpen(true);
            }}
            className="border-border bg-background hover:bg-muted inline-flex items-center gap-1.5 rounded border px-3 py-1.5 text-sm font-medium"
          >
            <Plus className="h-4 w-4" />
            {t('partners:bankAccounts.add', 'Hesap Ekle')}
          </button>
        )}
      </div>

      {/* Form */}
      {canEdit && isFormOpen && (
        <div className="border-border bg-card space-y-4 rounded border p-4">
          <h4 className="font-medium">
            {editingId
              ? t('partners:bankAccounts.edit', 'Hesap Düzenle')
              : t('partners:bankAccounts.new', 'Yeni Banka Hesabı')}
          </h4>
          <form noValidate onSubmit={handleSubmit} className="space-y-3">
            <fieldset disabled={isPending} className="space-y-3">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <div>
                  <label
                    htmlFor="cari-bank-name"
                    className="text-muted-foreground mb-1 block text-xs font-medium"
                  >
                    {t('partners:bankAccounts.bankName', 'Banka Adı')} *
                  </label>
                  <input
                    id="cari-bank-name"
                    aria-invalid={bankNameError}
                    aria-describedby={bankNameError ? 'cari-bank-name-error' : undefined}
                    value={form.bankName}
                    onChange={(e) => {
                      setForm({ ...form, bankName: e.target.value });
                      setBankNameError(false);
                    }}
                    aria-required="true"
                    className="border-border bg-background focus:border-foreground w-full rounded border px-3 py-1.5 text-sm focus:outline-none"
                  />
                  {bankNameError && (
                    <p
                      role="alert"
                      id="cari-bank-name-error"
                      className="text-destructive mt-1 text-xs"
                    >
                      {t('travelCari:account.validation.bankName')}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-muted-foreground mb-1 block text-xs font-medium">
                    {t('partners:bankAccounts.accountName', 'Hesap Adı')}
                  </label>
                  <input
                    value={form.accountName}
                    onChange={(e) => setForm({ ...form, accountName: e.target.value })}
                    className="border-border bg-background focus:border-foreground w-full rounded border px-3 py-1.5 text-sm focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-muted-foreground mb-1 block text-xs font-medium">
                    {t('partners:bankAccounts.currency', 'Para Birimi')}
                  </label>
                  <select
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    className="border-border bg-background focus:border-foreground w-full rounded border px-3 py-1.5 text-sm focus:outline-none"
                  >
                    <option value="TRY">TRY</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
                <div className="col-span-2 sm:col-span-3">
                  <label className="text-muted-foreground mb-1 block text-xs font-medium">
                    IBAN
                  </label>
                  <input
                    value={form.iban}
                    onChange={(e) => setForm({ ...form, iban: e.target.value })}
                    placeholder="TR00 0000 0000 0000 0000 0000 00"
                    className="border-border bg-background focus:border-foreground w-full rounded border px-3 py-1.5 font-mono text-sm focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-muted-foreground mb-1 block text-xs font-medium">
                    {t('partners:bankAccounts.accountNo', 'Hesap No')}
                  </label>
                  <input
                    value={form.accountNo}
                    onChange={(e) => setForm({ ...form, accountNo: e.target.value })}
                    className="border-border bg-background focus:border-foreground w-full rounded border px-3 py-1.5 text-sm focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-muted-foreground mb-1 block text-xs font-medium">
                    {t('partners:bankAccounts.branchCode', 'Şube Kodu')}
                  </label>
                  <input
                    value={form.branchCode}
                    onChange={(e) => setForm({ ...form, branchCode: e.target.value })}
                    className="border-border bg-background focus:border-foreground w-full rounded border px-3 py-1.5 text-sm focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-muted-foreground mb-1 block text-xs font-medium">
                    {t('partners:bankAccounts.branchName', 'Şube Adı')}
                  </label>
                  <input
                    value={form.branchName}
                    onChange={(e) => setForm({ ...form, branchName: e.target.value })}
                    className="border-border bg-background focus:border-foreground w-full rounded border px-3 py-1.5 text-sm focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-muted-foreground mb-1 block text-xs font-medium">
                    SWIFT
                  </label>
                  <input
                    value={form.swift}
                    onChange={(e) => setForm({ ...form, swift: e.target.value })}
                    className="border-border bg-background focus:border-foreground w-full rounded border px-3 py-1.5 text-sm focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-muted-foreground mb-1 block text-xs font-medium">
                    {t('partners:bankAccounts.accountType', 'Hesap Türü')}
                  </label>
                  <input
                    value={form.accountType}
                    onChange={(e) => setForm({ ...form, accountType: e.target.value })}
                    placeholder="Vadesiz, Vadeli..."
                    className="border-border bg-background focus:border-foreground w-full rounded border px-3 py-1.5 text-sm focus:outline-none"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.isPrimary}
                      onChange={(e) => setForm({ ...form, isPrimary: e.target.checked })}
                      className="border-border rounded"
                    />
                    {t('partners:bankAccounts.primary', 'Birincil Hesap')}
                  </label>
                </div>
              </div>
              <div>
                <label className="text-muted-foreground mb-1 block text-xs font-medium">
                  {t('common:notes', 'Notlar')}
                </label>
                <input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="border-border bg-background focus:border-foreground w-full rounded border px-3 py-1.5 text-sm focus:outline-none"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={isPending}
                  className="bg-foreground text-background hover:bg-foreground/90 rounded px-4 py-1.5 text-sm font-medium disabled:opacity-50"
                >
                  {editingId ? t('common:save', 'Kaydet') : t('common:add', 'Ekle')}
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  className="border-border hover:bg-muted rounded border px-4 py-1.5 text-sm"
                >
                  {t('common:cancel', 'İptal')}
                </button>
              </div>
            </fieldset>
          </form>
        </div>
      )}

      {/* Account List */}
      {(!accounts || accounts.length === 0) && !isFormOpen ? (
        <div className="border-border bg-card flex flex-col items-center justify-center rounded border py-12 text-center">
          <Building2 className="text-muted-foreground/40 h-10 w-10" />
          <p className="text-muted-foreground mt-3 text-sm">
            {t('partners:bankAccounts.empty', 'Henüz banka hesabı eklenmemiş')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {accounts?.map((account) => (
            <div
              key={account.id}
              className="border-border bg-card flex items-start justify-between rounded border p-4"
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{account.bankName}</span>
                  <span className="bg-muted rounded px-1.5 py-0.5 text-xs">{account.currency}</span>
                  {account.isPrimary && (
                    <span className="inline-flex items-center gap-0.5 rounded bg-yellow-100 px-1.5 py-0.5 text-xs text-yellow-700">
                      <Star className="h-3 w-3" />
                      {t('partners:bankAccounts.primary', 'Birincil')}
                    </span>
                  )}
                </div>
                {account.accountName && (
                  <p className="text-muted-foreground text-sm">{account.accountName}</p>
                )}
                {account.iban && <p className="font-mono text-sm">{account.iban}</p>}
                <div className="text-muted-foreground flex gap-4 text-xs">
                  {account.accountNo && <span>Hesap: {account.accountNo}</span>}
                  {account.branchName && <span>Şube: {account.branchName}</span>}
                  {account.branchCode && <span>Şube Kodu: {account.branchCode}</span>}
                  {account.swift && <span>SWIFT: {account.swift}</span>}
                </div>
                {account.notes && (
                  <p className="text-muted-foreground text-xs italic">{account.notes}</p>
                )}
              </div>
              {canEdit && (
                <div className="flex gap-1">
                  <button
                    disabled={isPending}
                    onClick={() => openEdit(account)}
                    className="hover:bg-muted rounded p-1.5"
                    title={t('common:edit', 'Düzenle')}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  {deleteConfirmId === account.id ? (
                    <div className="flex gap-1">
                      <button
                        disabled={partnerDelete.isPending || customerDelete.isPending}
                        onClick={() =>
                          entityType === 'partner'
                            ? partnerDelete.mutate({ id: account.id })
                            : customerDelete.mutate({ id: account.id })
                        }
                        className="rounded bg-red-100 px-2 py-1 text-xs text-red-700 hover:bg-red-200"
                      >
                        {t('common:confirm', 'Onayla')}
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(null)}
                        className="hover:bg-muted rounded px-2 py-1 text-xs"
                      >
                        {t('common:cancel', 'İptal')}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirmId(account.id)}
                      className="rounded p-1.5 text-red-500 hover:bg-red-50"
                      title={t('common:delete', 'Sil')}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
